// Money formatter that reads the active currency from localStorage. The
// active company sets this on /api/company load and on PUT /api/company.
// Falls back to CRC.

const STORAGE_KEY = "company_currency";

export const setActiveCurrency = (code) => {
  const normalized = (code || "CRC").toUpperCase();
  localStorage.setItem(STORAGE_KEY, normalized);
  try {
    window.dispatchEvent(new CustomEvent("currency-changed", { detail: normalized }));
  } catch { /* ignore */ }
};

export const getActiveCurrency = () => {
  const v = (localStorage.getItem(STORAGE_KEY) || "CRC").toUpperCase();
  return v === "USD" ? "USD" : "CRC";
};

export const currencySymbol = (code) => {
  const c = (code || getActiveCurrency()).toUpperCase();
  return c === "USD" ? "$" : "₡";
};

export const formatMoney = (amount, opts = {}) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  const code = (opts.currency || getActiveCurrency()).toUpperCase();
  const symbol = currencySymbol(code);
  // \u00A0 = non-breaking space: keeps "₡" and the number together on the
  // same line, even in narrow columns / wrapped cells. CSS-level
  // `whitespace-nowrap` was unreliable because some pages override it.
  const NBSP = "\u00A0";
  if (code === "CRC") {
    // No decimals, dot thousands separator (CR convention).
    const rounded = Math.round(num);
    const s = Math.abs(rounded).toLocaleString("es-CR");
    return `${rounded < 0 ? "-" : ""}${symbol}${NBSP}${s}`;
  }
  // USD: 2 decimals, comma thousands separator.
  return `${num < 0 ? "-" : ""}${symbol}${NBSP}${Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

export const formatMoneyPlain = (amount) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "0";
  const code = getActiveCurrency();
  if (code === "CRC") return Math.round(num).toLocaleString("es-CR");
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Compact representation for KPI/stat cards where space is tight.
 *  Examples (CRC): 950 → "₡ 950", 12 453 → "₡ 12,5K", 3 280 870 → "₡ 3,3M",
 *  1 200 000 000 → "₡ 1,2B". Always keeps the currency symbol. */
export const formatMoneyCompact = (amount, opts = {}) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  const code = (opts.currency || getActiveCurrency()).toUpperCase();
  const symbol = currencySymbol(code);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs < 10000) {
    // Small numbers: show full precision (no abbreviation needed).
    return formatMoney(num, opts);
  }
  const fmt = (v, suffix) => `${sign}${symbol}\u00A0${v.toLocaleString("es-CR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}${suffix}`;
  if (abs >= 1_000_000_000) return fmt(abs / 1_000_000_000, "B");
  if (abs >= 1_000_000)     return fmt(abs / 1_000_000, "M");
  return fmt(abs / 1000, "K");
};
